// src/features/inventory/pages/ItemDetailPage/tabs/index.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CrisisAlert,
  History as HistoryIcon,
  InfoOutlined as InfoIcon,
  LocalShipping as LocalShippingIcon,
  Payments as PaymentsIcon,
  QrCode2 as QrCode2Icon,
} from '@mui/icons-material';
import BasicInfo from './BasicInfo';
import StockLog from './StockLog';
import Suppliers from './Suppliers';
import BatchNumbers from './BatchNumbers';
import Hazardous from './Hazardous';
import Value from './Value';
import { usePermissions } from '../../../../auth/hooks/usePermissions';

function TabPanel({ children, value, index, sx }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`item-tabpanel-${index}`}
      aria-labelledby={`item-tab-${index}`}
      sx={value !== index ? null : { ...sx, minHeight: 0 }}
    >
      {value === index && <Box sx={{ minHeight: 0, ...sx }}>{children}</Box>}
    </Box>
  );
}

function a11yProps(index) {
  return {
    id: `item-tab-${index}`,
    'aria-controls': `item-tabpanel-${index}`,
  };
}

export default function ItemTabs({ itemId, item, onStockChanged, onMsdsChanged }) {
  const { t } = useTranslation(['inventory', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasScope } = usePermissions();

  const lotTracked = item?.product_group?.lot_tracked ?? false;
  const inventoryTracked = item?.product_group?.inventory_tracked ?? false;
  const hazardous = item?.product_group?.hazardous ?? false;
  // Changes when stock is booked anywhere on the page, so every tab showing stock refetches
  const refreshKey = item?.last_stock_transaction?.stock_transaction_id;

  const tabs = [
    {
      key: 'basic_info',
      label: t('inventory:items.tabs.basic_info.title'),
      description: t('inventory:items.tabs.basic_info.description'),
      icon: InfoIcon,
      component: BasicInfo,
      props: { item, itemId },
    },
    ...(inventoryTracked && hasScope('inventory:stock:read') ? [{
      key: 'stock_log',
      label: t('inventory:items.tabs.stock_log.title'),
      description: t('inventory:items.tabs.stock_log.description'),
      icon: HistoryIcon,
      component: StockLog,
      props: { itemId, lotTracked, refreshKey },
    }] : []),
    ...(lotTracked && hasScope('inventory:lot:read') ? [{
      key: 'batches',
      label: t('inventory:items.tabs.batches.title'),
      description: t('inventory:items.tabs.batches.description'),
      icon: QrCode2Icon,
      component: BatchNumbers,
      props: { itemId, uom: item?.uom, refreshKey, onStockChanged },
    }] : []),
    // Untracked items have no stock to value, but still a price worth following
    ...(hasScope('inventory:cost:read') ? [{
      key: 'value',
      label: t('inventory:items.tabs.value.title'),
      description: inventoryTracked
        ? t('inventory:items.tabs.value.description')
        : t('inventory:items.tabs.value.description_untracked'),
      icon: PaymentsIcon,
      component: Value,
      props: { itemId, uom: item?.uom, refreshKey, lotTracked, inventoryTracked },
    }] : []),
    ...(hasScope('inventory:supplier:read') ? [{
      key: 'suppliers',
      label: t('inventory:items.tabs.suppliers.title'),
      description: t('inventory:items.tabs.suppliers.description'),
      icon: LocalShippingIcon,
      component: Suppliers,
      props: { itemId },
    }] : []),
    ...(hazardous && hasScope('inventory:msds:read') ? [{
      key: 'hazardous',
      label: t('inventory:items.tabs.hazardous.title'),
      description: t('inventory:items.tabs.hazardous.description'),
      icon: CrisisAlert,
      component: Hazardous,
      props: { itemId, item, onMsdsChanged },
    }] : []),
  ];

  const getTabFromParams = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const idx = tabs.findIndex(tab => tab.key === tabParam);
      return idx !== -1 ? idx : 0;
    }
    return 0;
  };

  const [currentTab, setCurrentTab] = useState(getTabFromParams);

  useEffect(() => {
    setCurrentTab(getTabFromParams());
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabs[newValue].key);
    setSearchParams(newParams);
  };

  return (
    <Box sx={{ width: '100%', mt: 3, flex: 1, minHeight: 0 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
          height: '100%',
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="item detail tabs"
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              minHeight: isMobile ? 56 : 64,
              py: isMobile ? 1.5 : 2,
              px: isMobile ? 1.5 : 3,
              '&.Mui-selected': { fontWeight: 600, bgcolor: 'background.paper' },
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            return (
              <Tab
                key={tab.key}
                icon={<Icon sx={{ fontSize: isMobile ? 34 : 36 }} />}
                iconPosition="start"
                label={
                  <Box sx={{ textAlign: 'left', ml: isMobile ? 0.5 : 1, pb: 1 }}>
                    <Typography variant="body2" fontWeight="inherit">{tab.label}</Typography>
                    {!isMobile && (
                      <Typography variant="caption" color="text.secondary">{tab.description}</Typography>
                    )}
                  </Box>
                }
                sx={{ alignItems: 'center', justifyContent: 'flex-start', minWidth: 'unset', height: "100%" }}
                {...a11yProps(index)}
              />
            );
          })}
        </Tabs>

        <Box sx={{ bgcolor: 'background.paper', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {tabs.map((tab, index) => {
            const TabComponent = tab.component;
            return (
              <TabPanel 
                key={tab.key} 
                value={currentTab} 
                index={index} 
                sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <TabComponent {...tab.props} />
              </TabPanel>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}
